import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { useEffect } from "react";
import { AnimationProvider } from "./lib/animations";
import { QueryProvider } from "./providers/QueryProvider";
import { useBundle, queryKeys } from "./hooks/useApi";
import { useQueryClient } from "@tanstack/react-query";
import Header from "./components/Header";
import Footer from "./components/Footer";
import RouteTransition from "./components/RouteTransition";
import LoadingProgressIndicator from "./components/LoadingProgressIndicator";
import ScrollRestoration from "./components/ScrollRestoration";
import PerformanceMonitorToggle from "./components/PerformanceMonitorToggle";
import HomePage from "./pages/HomePage";
import AllHomesPage from "./pages/AllHomesPage";
import CategoryPage from "./pages/CategoryPage";
import HomeDetailPage from "./pages/HomeDetailPage";
import ServicesPage from "./pages/ServicesPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import StatisticsDemo from "./pages/StatisticsDemo";
import AccessibilityTestPage from "./components/AccessibilityTestPage";

function AppContent() {
  // Use the bundle API for a single, fast request
  const { data: bundle, isLoading: loading } = useBundle();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (bundle) {
      console.log(`✅ Bundle loaded with ${bundle.pages.length} pages`);
      console.log(`📦 Last updated: ${bundle.lastUpdated}`);

      // Cache individual pages for instant access
      bundle.pages.forEach((page) => {
        queryClient.setQueryData(queryKeys.page(page.slug), page);
      });

      console.log(`💾 Cached ${bundle.pages.length} pages for instant navigation`);
    }
  }, [bundle, queryClient]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF5A1F] mx-auto mb-4"></div>
          <p className="text-[#334155]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen">
        {/* Global loading progress indicator */}
        <LoadingProgressIndicator position="top" />

        {/* Scroll restoration */}
        <ScrollRestoration enabled={true} smooth={true} />

        <Header />
        <main>
          <RouteTransition transitionType="fade" showLoadingScreen={false}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/homes" element={<AllHomesPage />} />
              <Route path="/homes/:category" element={<CategoryPage />} />
              <Route
                path="/homes/:category/:slug"
                element={<HomeDetailPage />}
              />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/statistics-demo" element={<StatisticsDemo />} />
              <Route
                path="/accessibility-test"
                element={<AccessibilityTestPage />}
              />
            </Routes>
          </RouteTransition>
        </main>
        <Footer />

        {/* Performance monitoring (development only) */}
        <PerformanceMonitorToggle />
      </div>
    </Router>
  );
}

function App() {
  return (
    <QueryProvider>
      <AnimationProvider>
        <AppContent />
      </AnimationProvider>
    </QueryProvider>
  );
}

export default App;
